import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Navbar } from '@/components/navbar'
import { setBusinessTimezone } from '@/lib/time'
import { cn } from '@/lib/utils'
import { usePublicSettings } from '@/hooks/usePublicSettings'
import { useAuthStore } from '@/stores/authStore'

interface PublicLayoutProps {
  children?: React.ReactNode
  className?: string
  showNavbar?: boolean
}

export function PublicLayout({
  children,
  className,
  showNavbar = true,
}: PublicLayoutProps) {
  const location = useLocation()
  const { data: publicSettings } = usePublicSettings()
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'
  const isAuthRoute = location.pathname.startsWith('/auth/')
  const showMaintenancePage =
    Boolean(publicSettings?.maintenanceMode) && !isAdmin && !isAuthRoute
  const copyrightText =
    publicSettings?.copyrightText || '© 2025-至今 次梦领域. All rights reserved.'
  const beianNumber = publicSettings?.beianNumber?.trim()

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  useEffect(() => {
    setBusinessTimezone(publicSettings?.businessTimezone)
  }, [publicSettings?.businessTimezone])

  return (
    <div className="flex min-h-screen flex-col bg-background transition-colors duration-300">
      {showNavbar && <Navbar />}

      <main
        className={cn(
          'flex flex-1 flex-col transition-colors duration-300',
          showNavbar && 'pt-0',
          className
        )}
      >
        {showMaintenancePage ? (
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(99,102,241,0.18),_transparent_38%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.14),_transparent_34%)]" />
            <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px)] [background-size:32px_32px]" />
            <div className="relative flex min-h-[calc(100vh-64px-76px)] items-center justify-center px-4 py-16">
              <div className="app-surface w-full max-w-4xl p-8 shadow-[0_32px_80px_rgba(15,23,42,0.16)] md:p-12">
                <div className="grid items-center gap-10 md:grid-cols-[0.9fr_1.1fr]">
                  <div className="flex justify-center">
                    <div className="app-card-soft flex h-40 w-40 items-center justify-center rounded-[2rem] shadow-[0_24px_60px_rgba(99,102,241,0.18)]">
                      <img
                        src="/site-logo.svg"
                        alt={publicSettings?.siteName || '次梦领域'}
                        className="h-24 w-auto"
                        style={{ imageRendering: 'pixelated' }}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.32em] text-indigo-500">
                      Maintenance
                    </p>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                      {publicSettings?.siteName || '次梦领域'} 正在维护中
                    </h1>
                    <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-400">
                      {publicSettings?.maintenanceMessage || '站点维护中，请稍后再试'}
                    </p>
                    <div className="mt-8 grid gap-3 sm:grid-cols-3">
                      <div className="app-card-soft rounded-[1.4rem] px-4 py-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">状态</p>
                        <p className="mt-2 text-sm font-semibold text-amber-600 dark:text-amber-300">
                          维护中
                        </p>
                      </div>
                      <div className="app-card-soft rounded-[1.4rem] px-4 py-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">访问</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          暂停普通用户访问
                        </p>
                      </div>
                      <div className="app-card-soft rounded-[1.4rem] px-4 py-4">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">说明</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          管理后台仍可正常使用
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          children ?? <Outlet />
        )}
      </main>

      <footer className="border-t border-border/80 bg-card py-6 transition-colors duration-300 dark:border-[#2a2b2e] dark:bg-[#1a1b1d]">
        <div className="mx-auto max-w-7xl px-4 text-center text-sm text-muted-foreground sm:px-6 lg:px-8">
          <p>{copyrightText}</p>
          {beianNumber ? (
            <p className="mt-1">
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noreferrer"
                className="transition-colors hover:text-slate-700 dark:hover:text-slate-200"
              >
                {beianNumber}
              </a>
            </p>
          ) : null}
        </div>
      </footer>
    </div>
  )
}
